import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp, Download, Calendar, DollarSign, Users, Target,
  Activity, MapPin, AlertCircle, ChevronRight,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, FunnelChart, Funnel, LabelList, Cell,
} from "recharts";
import { api, formatBRL } from "../../lib/api";

const PERIODS = [
  { v: "today", label: "Hoje" },
  { v: "7d", label: "7 dias" },
  { v: "30d", label: "30 dias" },
  { v: "12m", label: "12 meses" },
];

const COLORS = ["#D4AF37", "#0A1128", "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6"];

function StatCard({ label, value, icon: Icon, hint, accent = "#0A1128", testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</div>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div className="text-3xl font-black text-[#0A1128] tabular-nums leading-none">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-2">{hint}</div>}
    </div>
  );
}

function PeriodPicker({ value, onChange }) {
  return (
    <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm" data-testid="period-picker">
      {PERIODS.map((p) => (
        <button
          key={p.v}
          type="button"
          onClick={() => onChange(p.v)}
          data-testid={`period-${p.v}`}
          className={[
            "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
            value === p.v ? "bg-[#0A1128] text-white" : "text-gray-600 hover:text-[#0A1128]",
          ].join(" ")}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function DownloadCSV({ period }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="inline-flex bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {["reservations", "payments", "events"].map((k) => (
        <a
          key={k}
          href={`${BACKEND}/api/admin/metrics/export?kind=${k}&period=${period}`}
          data-testid={`export-${k}`}
          className="px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 border-r border-gray-100 last:border-r-0 inline-flex items-center gap-1"
        >
          <Download size={12} /> {k}
        </a>
      ))}
    </div>
  );
}

export default function Metrics() {
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [spaces, setSpaces] = useState(null);
  const [sources, setSources] = useState(null);
  const [abandonment, setAbandonment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/admin/metrics/overview").then((r) => r.data),
      api.get("/admin/metrics/funnel", { params: { period } }).then((r) => r.data),
      api.get("/admin/metrics/revenue", { params: { period } }).then((r) => r.data),
      api.get("/admin/metrics/spaces", { params: { period } }).then((r) => r.data),
      api.get("/admin/metrics/sources", { params: { period } }).then((r) => r.data),
      api.get("/admin/metrics/abandonment", { params: { period } }).then((r) => r.data),
    ])
      .then(([o, f, r, s, src, ab]) => {
        if (!mounted) return;
        setOverview(o); setFunnel(f); setRevenue(r);
        setSpaces(s); setSources(src); setAbandonment(ab);
      })
      .catch((e) => { if (mounted) console.error("metrics load failed", e); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [period]);

  const funnelData = useMemo(() => {
    if (!funnel) return [];
    return funnel.steps.map((s, i) => ({
      name: s.label,
      value: s.count,
      fill: COLORS[i % COLORS.length],
    }));
  }, [funnel]);

  const [activeFunnel, setActiveFunnel] = useState("acquisition");
  const activeFunnelData = useMemo(() => {
    if (!funnel || !funnel[activeFunnel]) return null;
    const f = funnel[activeFunnel];
    return {
      title: f.title,
      conversion_pct: f.conversion_pct,
      steps: f.steps.map((s, i) => ({
        name: s.label, value: s.count, fill: COLORS[i % COLORS.length],
      })),
    };
  }, [funnel, activeFunnel]);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="metrics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-[#0A1128]">Dashboard Executivo</h1>
          <p className="text-gray-600 text-sm">Funil de conversão, faturamento e desempenho dos ambientes.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodPicker value={period} onChange={setPeriod} />
          <DownloadCSV period={period} />
        </div>
      </div>

      {/* Top stats: Mês */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Receita do mês"
          value={overview ? formatBRL(overview.month_revenue) : "—"}
          icon={DollarSign} accent="#10B981" testid="stat-month-revenue"
          hint="Faturamento confirmado neste mês"
        />
        <StatCard
          label="Reservas confirmadas"
          value={overview?.confirmed_count ?? "—"}
          icon={Users} accent="#3B82F6" testid="stat-reservations"
          hint="Mês atual"
        />
        <StatCard
          label="Taxa de conversão"
          value={overview ? `${overview.conversion_pct}%` : "—"}
          icon={Target} accent="#D4AF37" testid="stat-conversion"
          hint="Visualizações → Pagamentos"
        />
        <StatCard
          label="Ticket médio"
          value={overview ? formatBRL(overview.ticket_avg) : "—"}
          icon={TrendingUp} accent="#8B5CF6" testid="stat-ticket-avg"
          hint="Por pagamento confirmado"
        />
      </div>

      {/* Funnel + Revenue chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="funnel-card">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="font-bold text-[#0A1128]">{activeFunnelData?.title || "Funil"}</h2>
            <span className="text-xs text-gray-500">{activeFunnelData?.conversion_pct ?? 0}% conversão final</span>
          </div>
          <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-4">
            {[
              { v: "acquisition", label: "Aquisição" },
              { v: "tour", label: "Tour" },
              { v: "packages", label: "Pacotes" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setActiveFunnel(t.v)}
                data-testid={`funnel-tab-${t.v}`}
                className={[
                  "px-3 py-1.5 rounded-md text-xs font-semibold transition-all",
                  activeFunnel === t.v ? "bg-white text-[#0A1128] shadow-sm" : "text-gray-600 hover:text-[#0A1128]",
                ].join(" ")}
              >{t.label}</button>
            ))}
          </div>
          {activeFunnelData?.steps?.length > 0 && (
            <div className="space-y-2">
              {activeFunnelData.steps.map((s, i) => {
                const max = activeFunnelData.steps[0].value || 1;
                const pct = Math.max((s.value / max) * 100, 4);
                return (
                  <div key={s.name} data-testid={`funnel-step-${i}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{s.name}</span>
                      <span className="font-bold tabular-nums text-[#0A1128]">{s.value.toLocaleString("pt-BR")}</span>
                    </div>
                    <div className="h-7 rounded-md bg-gray-100 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: s.fill }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="revenue-card">
          <h2 className="font-bold text-[#0A1128] mb-4">Receita diária (período)</h2>
          {revenue?.daily_series?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={revenue.daily_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#9CA3AF" />
                <YAxis fontSize={11} stroke="#9CA3AF" />
                <Tooltip formatter={(v) => formatBRL(v)} />
                <Line type="monotone" dataKey="total" stroke="#D4AF37" strokeWidth={3} dot={{ fill: "#0A1128", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              Sem pagamentos no período
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-gray-100">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Hoje</div>
              <div className="font-bold text-[#0A1128]">{revenue ? formatBRL(revenue.today) : "—"}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Mês</div>
              <div className="font-bold text-[#0A1128]">{revenue ? formatBRL(revenue.month) : "—"}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">12 meses</div>
              <div className="font-bold text-[#0A1128]">{revenue ? formatBRL(revenue.year) : "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Spaces + Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="spaces-card">
          <h2 className="font-bold text-[#0A1128] mb-1">Ambientes mais reservados</h2>
          <p className="text-xs text-gray-500 mb-4">Ocupação no período: <strong>{spaces?.occupancy_pct ?? 0}%</strong> de {spaces?.capacity_slots ?? 0} slots</p>
          {spaces?.by_space?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(220, spaces.by_space.length * 32)}>
              <BarChart data={spaces.by_space} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" fontSize={11} stroke="#9CA3AF" />
                <YAxis dataKey="space_name" type="category" fontSize={10} stroke="#374151" width={130} />
                <Tooltip />
                <Bar dataKey="count" fill="#D4AF37" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">Sem reservas no período</div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="hours-card">
          <h2 className="font-bold text-[#0A1128] mb-4">Horários mais reservados</h2>
          {spaces?.by_hour?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={spaces.by_hour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="hour" fontSize={11} stroke="#9CA3AF" />
                <YAxis fontSize={11} stroke="#9CA3AF" />
                <Tooltip />
                <Bar dataKey="count" fill="#0A1128" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Sem dados</div>
          )}
        </div>
      </div>

      {/* Sources + Abandonment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="sources-card">
          <h2 className="font-bold text-[#0A1128] mb-4">Origem das reservas</h2>
          {sources?.by_source?.length ? (
            <div className="space-y-3">
              {sources.by_source.map((s, i) => (
                <div key={s.source} className="flex items-center justify-between text-sm" data-testid={`source-${s.source}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="font-semibold text-[#0A1128] capitalize truncate">{s.source.replace(/_/g, " ")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-600 tabular-nums">{s.reservations} reservas</span>
                    <span className="font-bold text-[#0A1128] tabular-nums">{formatBRL(s.revenue)}</span>
                    <span className={`px-2 py-0.5 rounded font-bold tabular-nums ${s.conversion_pct >= 50 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{s.conversion_pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">Nenhuma reserva ainda</div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="abandonment-card">
          <h2 className="font-bold text-[#0A1128] mb-4">Checkout abandonado</h2>
          {abandonment ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-amber-50 rounded-lg p-3" data-testid="ab-pending">
                <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">Pendentes</div>
                <div className="text-2xl font-bold text-amber-900 tabular-nums">{abandonment.pending}</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3" data-testid="ab-confirmed">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Pagos</div>
                <div className="text-2xl font-bold text-emerald-900 tabular-nums">{abandonment.confirmed}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3" data-testid="ab-expired">
                <div className="text-xs uppercase tracking-widest text-red-700 font-bold">Expiraram</div>
                <div className="text-2xl font-bold text-red-900 tabular-nums">{abandonment.expired}</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3" data-testid="ab-recovered">
                <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Recuperados</div>
                <div className="text-2xl font-bold text-blue-900 tabular-nums">{abandonment.recovered_by_email}</div>
              </div>
              <div className="col-span-2 text-center text-sm pt-2 border-t border-gray-100">
                Taxa de recuperação: <strong className="text-[#0A1128]">{abandonment.recovery_rate_pct}%</strong>
              </div>
            </div>
          ) : <div className="text-gray-400 text-sm">—</div>}
        </div>
      </div>

      {/* Last reservations */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="last-reservations-card">
        <h2 className="font-bold text-[#0A1128] mb-4">Últimas reservas</h2>
        {overview?.last_reservations?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-gray-500 font-bold border-b border-gray-200">
                  <th className="text-left py-2 pr-2">Cliente</th>
                  <th className="text-left py-2 px-2 hidden sm:table-cell">Ambiente</th>
                  <th className="text-left py-2 px-2 hidden md:table-cell">Início</th>
                  <th className="text-right py-2 px-2">Valor</th>
                  <th className="text-center py-2 pl-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {overview.last_reservations.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50">
                    <td className="py-2 pr-2 truncate">{r.guest_name || r.client_name || "—"}</td>
                    <td className="py-2 px-2 hidden sm:table-cell text-gray-600 truncate">{r.space_name}</td>
                    <td className="py-2 px-2 hidden md:table-cell text-gray-500 text-xs">
                      {new Date(r.start_time).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="py-2 px-2 text-right font-bold tabular-nums">{formatBRL(r.price || 0)}</td>
                    <td className="py-2 pl-2 text-center">
                      <span className={[
                        "text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded",
                        r.status === "confirmed" ? "bg-emerald-100 text-emerald-700"
                          : r.status === "pending_payment" ? "bg-amber-100 text-amber-700"
                          : r.status === "expired" ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-600",
                      ].join(" ")}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-gray-400 text-sm">Nenhuma reserva registrada</div>}
      </div>

      {loading && <div className="text-center text-xs text-gray-400">Atualizando dados...</div>}
    </div>
  );
}
